import _ from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {CrmToFrontCustomFieldMapping} from '../../../../../../../../src/components/accounts/crm/common/crmSyncHelpers';
import {CrmMetadataModel} from '../../../../../../../../src/models/api/crmMetadataModel';
import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncModel,
  CrmSyncSourcesEnum,
  CrmSyncStatusesEnum,
} from '../../../../../../../../src/models/api/crmSyncModel';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../../shared/helpers/src/types/asyncStatuses';
import {useCrmFields, useMetadata} from './crmSyncsHooks';
import {CrmSyncsMappingContainer} from './crmSyncsMapping/crmSyncsMappingContainer';
import {CrmSyncOptionsEnum, EXTERNAL_LINK_SYNC_ALL, SelectedOption} from './crmSyncsOptions/crmSyncsOptions';
import {CrmSyncsOptionsContainer} from './crmSyncsOptions/crmSyncsOptionsContainer';
import {CrmSyncsPageResourceConfig} from './crmSyncsPageConfig/crmSyncsPageConfig';
import {CrmSyncsSectionHeader} from './crmSyncsSectionHeader';

/*
 * Types.
 */

export interface CrmSyncsFormModel {
  isEnabled: boolean;
  selectedListOption: SelectedOption;
  mappedFields: ReadonlyArray<CrmToFrontCustomFieldMapping>;
  hasError: boolean;
}

/*
 * Props.
 */

export interface CrmSyncsFormProps {
  panelId: ContactsManagerPanelId;
  source: CrmSyncSourcesEnum;
  isConnected: boolean;
  hasExpiredCredentials: boolean;
  crmSync: CrmSyncModel;
  config: CrmSyncsPageResourceConfig;
  isSaving: boolean;
  isLastForm: boolean;
  resourceType: CrmSyncIntegrationResourceTypesEnum;
  disabledOptionType?: CrmSyncOptionsEnum;
  onChange: HandlerOf<CrmSyncsFormModel>;
  setHasExpiredCredentials: HandlerOf<boolean>;
}

/*
 * Styles.
 */

export const SectionDividerDiv = styled.div`
  height: 1px;
  width: 600px;
  background-color: #e6e6e6;
`;

/*
 * Component.
 */

export const CrmSyncsForm: FC<CrmSyncsFormProps> = (props) => {
  const {
    source,
    isConnected,
    panelId,
    crmSync,
    hasExpiredCredentials,
    config,
    isSaving,
    isLastForm,
    disabledOptionType,
    resourceType,
    onChange,
    setHasExpiredCredentials,
  } = props;
  const [formState, setFormState] = useState<CrmSyncsFormModel>({
    isEnabled: crmSync.status === CrmSyncStatusesEnum.ENABLED,
    selectedListOption: computeInitialListOption(crmSync),
    mappedFields: computeInitialMappedFields(crmSync, config),
    hasError: false,
  });

  const isSelectedOptionDisabled = formState.selectedListOption.type === disabledOptionType;

  const isValidConnected = !hasExpiredCredentials && isConnected;
  const {metadata, status: metadataStatus} = useMetadata(
    crmSync.id,
    isValidConnected,
    setHasExpiredCredentials,
  );

  const onFormChange = (updatedFormState: CrmSyncsFormModel) => {
    const hasError = computeHasError(
      isValidConnected,
      metadata,
      config,
      isSaving,
      isSelectedOptionDisabled,
      updatedFormState.selectedListOption,
    );
    setFormState({...updatedFormState, hasError});
    onChange({...updatedFormState, hasError});
  };

  const {crmFields, status: fieldsStatus} = useCrmFields(
    crmSync.id,
    isValidConnected,
    setHasExpiredCredentials,
  );

  const isLoading =
    metadataStatus === AsyncStatusesEnum.LOADING || fieldsStatus === AsyncStatusesEnum.LOADING;
  const isSectionEnabled = formState.isEnabled;

  return (
    <>
      <SectionDividerDiv />
      <CrmSyncsSectionHeader
        resourceType={resourceType}
        isEnabled={isSectionEnabled}
        isConnected={isValidConnected}
        onChange={(isEnabled) => {
          onFormChange({...formState, isEnabled});
        }}
      />
      {isSectionEnabled && (
        <>
          <CrmSyncsOptionsContainer
            isLoading={isLoading}
            isDisabled={isSaving}
            source={source}
            resourceType={resourceType}
            allResourceCount={metadata?.crmResourceCount}
            selectedOption={formState.selectedListOption}
            disabledOptionType={disabledOptionType}
            onSelectOption={(newSelectedOption) => {
              onFormChange({...formState, selectedListOption: newSelectedOption});
            }}
          />
          <CrmSyncsMappingContainer
            panelId={panelId}
            selectedOption={formState.selectedListOption}
            crmSyncId={crmSync?.id}
            source={source}
            resourceType={resourceType}
            isDisabled={computeHasError(
              isValidConnected,
              metadata,
              config,
              isSaving,
              isSelectedOptionDisabled,
              formState.selectedListOption,
            )}
            mappedFields={formState.mappedFields}
            crmFields={crmFields}
            crmMetadata={metadata}
            setMappedFields={(newMappedFields) => {
              onFormChange({...formState, mappedFields: newMappedFields});
            }}
            isLoading={isLoading}
          />
        </>
      )}
      {isLastForm && !isSectionEnabled && <SectionDividerDiv />}
    </>
  );
};

/*
 * Helpers.
 */

function computeHasAllMaxCountError(
  config: CrmSyncsPageResourceConfig,
  crmResourceCount?: number,
  selectedOptionType?: CrmSyncOptionsEnum,
) {
  return Boolean(
    crmResourceCount &&
      crmResourceCount > config.maxNumberToSync &&
      selectedOptionType === CrmSyncOptionsEnum.ALL,
  );
}

function computeHasError(
  isValidConnected: boolean,
  metadata: CrmMetadataModel | undefined,
  config: CrmSyncsPageResourceConfig,
  isSaving: boolean,
  isSelectedOptionDisabled: boolean,
  selectedOption?: SelectedOption,
) {
  const hasNoListForListOption = selectedOption?.type === CrmSyncOptionsEnum.LISTS && !selectedOption.listId;
  const hasMaxCountError = computeHasAllMaxCountError(
    config,
    metadata?.crmResourceCount,
    selectedOption?.type,
  );
  return Boolean(
    !isValidConnected ||
      !selectedOption ||
      hasNoListForListOption ||
      isSaving ||
      isSelectedOptionDisabled ||
      hasMaxCountError,
  );
}

function computeInitialMappedFields(crmSync: CrmSyncModel, config: CrmSyncsPageResourceConfig) {
  if (Object.values(crmSync.mapping).length === 0) {
    // We return the optional default fields if there is not an existing mapping
    return config.syncMapping.computeOptionalMapping();
  }

  // Required fields are automatically added to the request (see useUpdateCrmSync)
  // and displayed in ui in a unique way so we do not need them here
  const requiredFields = config.syncMapping.computeRequiredMapping();
  return _(crmSync.mapping)
    .map((customFieldId, crmFieldId) => ({crmFieldId, customFieldId}))
    .filter(
      (mapping) =>
        !requiredFields.some((requiredMapping) => requiredMapping.crmFieldId === mapping.crmFieldId),
    )
    .value();
}

function computeInitialListOption(crmSync: CrmSyncModel): SelectedOption {
  const {externalListId} = crmSync.metadata;

  if (externalListId === EXTERNAL_LINK_SYNC_ALL) {
    return {
      type: CrmSyncOptionsEnum.ALL,
    };
  }
  if (!externalListId && crmSync.resourceType === CrmSyncIntegrationResourceTypesEnum.CARD) {
    return {
      type: CrmSyncOptionsEnum.ACCOUNTS,
    };
  }
  return {
    type: CrmSyncOptionsEnum.LISTS,
    listId: externalListId,
  };
}
